import type { PlanV2IssueCardPayload } from './plan-v2-issues';

/**
 * The evidence panel under an open Dashboard package row.
 *
 * This module owns the *arrangement*: which rows go in which bucket and in
 * what order. It owns none of the wording: the rows are the `/review` cards,
 * one each, worded in `dashboard-page-rows.ts` from the card's own data
 * (TECHNICAL_DEBT §0ck), so a row cannot drift from the card it stands for.
 *
 * The panel replaced a row of verdict chips that said a word and stopped:
 * `GAP FOUND` with no gap, `NO RIVALS AHEAD` with no rank. A verdict without
 * the fact behind it is a dead end — it costs a click to learn anything, which
 * is the opposite of what an expanded row is for.
 */

/**
 * Ordered worst-first, and the order is the whole type.
 *
 * `bad` is a loss already taken (a rival outranks you today). `warn` is
 * something to do. `neutral` is a measured result with no verdict attached.
 * `ok` passed. `muted` is the one that is not a result at all: the check did
 * not run.
 */
export type EvidenceTone = 'bad' | 'warn' | 'neutral' | 'ok' | 'muted';

const TONE_RANK: Record<EvidenceTone, number> = {
  bad: 0,
  warn: 1,
  neutral: 2,
  ok: 3,
  muted: 4
};

export interface PageEvidence {
  /** Stable per diagnosis — the `PlanV2IssueType`, or `internal_link`. */
  key: string;
  /** "Competition", "Supporting pages". Sans, the row's label column. */
  title: string;
  /** The verdict, as a `.badge .badge--caps` — "GAP FOUND", "#7, 2 AHEAD". */
  label: string;
  tone: EvidenceTone;
  /** One sentence naming the number behind the verdict. Never empty. */
  fact: string;
  /** Short mono chips under the fact: rival positions, missing terms, paths. */
  specifics: string[];
  /** Feeds `app-plan-v2-issue-card-actions`. Null where triage makes no sense. */
  payload: PlanV2IssueCardPayload | null;
}

export interface PageEvidencePanel {
  /** `bad` and `warn`, worst first, capped. Each renders a full row. */
  attention: PageEvidence[];
  /** How many `attention` rows the cap left off. 0 when none. */
  attentionHidden: number;
  /** `ok` and `neutral` — measured, nothing to do. One summary line. */
  clear: PageEvidence[];
  /** `muted` — the check did not run. Its own line, and NOT "clear". */
  unchecked: PageEvidence[];
}

/**
 * Three buckets, because there are three different things to say and the
 * middle one is the trap.
 *
 * `clear` and `unchecked` look identical if you only have a colour: a green
 * "Freshness" and a grey "Freshness" both read as "fine". They are not. One
 * means the page is fresher than the SERP; the other means the crawl found no
 * date and nothing was compared. Folding "not checked" into "clear" tells an
 * operator a check passed that never ran, which is the worst thing this panel
 * could do.
 *
 * **`attention` is no longer capped by default.** It was capped at three to
 * keep the panel scannable, and the cost was a line reading "+1 more diagnosis
 * need attention" — a row that says something is wrong and refuses to say
 * what. Operators hit it and could not tell whether the hidden row was an
 * internal-link suggestion or a critical technical fault, so the cap bought
 * brevity by making the panel untrustworthy. Length is the lesser problem: the
 * panel only opens on a row the operator deliberately expanded, and the rows
 * that used to fill it are now suppressed at source (an unreadable rival no
 * longer takes a slot to say so).
 *
 * `cap` is kept as a parameter — a caller with a genuinely tight surface can
 * still pass one, and `attentionHidden` still reports what it cost.
 */
export function buildPageEvidencePanel(
  rows: readonly PageEvidence[],
  cap = Number.POSITIVE_INFINITY
): PageEvidencePanel {
  const sorted = [...rows].sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone]);

  const attention = sorted.filter((r) => r.tone === 'bad' || r.tone === 'warn');
  const limit = Math.max(0, cap);

  return {
    attention: attention.slice(0, limit),
    attentionHidden: Math.max(0, attention.length - limit),
    clear: sorted.filter((r) => r.tone === 'ok' || r.tone === 'neutral'),
    unchecked: sorted.filter((r) => r.tone === 'muted')
  };
}

/** True when there is nothing at all to draw — the panel renders its own copy. */
export function isPageEvidenceEmpty(panel: PageEvidencePanel): boolean {
  return (
    panel.attention.length === 0 &&
    panel.clear.length === 0 &&
    panel.unchecked.length === 0
  );
}
